<template>
  <div class="tw-relative" :data-theme="theme">
    <button
      type="button"
      :disabled="disabled"
      @click="toggle"
      :class="[
        'tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-xl tw-px-4 tw-py-3 tw-text-sm tw-text-left tw-transition-colors focus:tw-outline-none disabled:tw-opacity-50 disabled:tw-cursor-not-allowed',
        isDark ? 'tw-bg-[#151719] tw-text-gray-100' : 'tw-bg-gray-100 tw-text-gray-900',
        open ? 'tw-ring-2 tw-ring-[#33b35a]/20' : ''
      ]"
    >
      <span class="tw-truncate" :class="selectedOption ? '' : (isDark ? 'tw-text-gray-400' : 'tw-text-gray-500')">
        <slot name="value" :selected="selectedOption">{{ selectedOption ? getLabel(selectedOption) : placeholder }}</slot>
      </span>
      <i class="fa-solid fa-chevron-down tw-text-[10px] tw-opacity-50 tw-transition-transform tw-shrink-0" :class="{ 'tw-rotate-180': open }"></i>
    </button>

    <div v-if="open" @click="close" class="tw-fixed tw-inset-0 tw-z-40"></div>

    <div
      v-if="open"
      :class="[
        'tw-absolute tw-z-50 tw-mt-2 tw-w-full tw-rounded-lg tw-shadow-xl tw-overflow-hidden',
        isDark ? 'tw-bg-[#0f1114]' : 'tw-bg-white'
      ]"
    >
      <div v-if="searchable" class="tw-p-2 tw-border-b" :class="isDark ? 'tw-border-gray-800' : 'tw-border-gray-100'">
        <div class="tw-relative">
          <i class="fa-solid fa-magnifying-glass tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-text-xs"></i>
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            :placeholder="searchPlaceholder"
            @keydown.esc.prevent="close"
            :class="[
              'tw-w-full tw-pl-8 tw-pr-2 tw-py-2 tw-text-xs tw-rounded-md',
              isDark ? 'tw-bg-gray-900 tw-text-gray-200' : 'tw-bg-gray-50 tw-text-gray-700'
            ]"
          />
        </div>
      </div>

      <div class="tw-max-h-64 tw-overflow-y-auto tw-py-1 base-select-scroll">
        <template v-for="(grupo, gi) in filteredGroups" :key="gi">
          <div
            v-if="grupo.label"
            class="tw-px-3 tw-pt-2 tw-pb-1 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider"
            :class="isDark ? 'tw-text-gray-400' : 'tw-text-gray-500'"
          >
            {{ grupo.label }}
          </div>
          <button
            type="button"
            v-for="(option, oi) in grupo.options"
            :key="`${gi}-${oi}`"
            @click="select(option)"
            :class="[
              'tw-w-full tw-text-left tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-py-2.5 tw-text-xs',
              isSelected(option)
                ? 'tw-bg-[#33b35a]/15 tw-text-[#2b9e4f] tw-font-semibold'
                : (isDark ? 'tw-text-gray-300 hover:tw-bg-[#33b35a] hover:tw-text-white' : 'tw-text-gray-800 hover:tw-bg-[#33b35a] hover:tw-text-white')
            ]"
          >
            <span class="tw-truncate">
              <slot name="option" :option="option" :selected="isSelected(option)">{{ getLabel(option) }}</slot>
            </span>
            <i v-if="isSelected(option)" class="fa-solid fa-check tw-text-[10px] tw-shrink-0"></i>
          </button>
        </template>

        <div v-if="filteredGroups.length === 0" class="tw-px-4 tw-py-6 tw-text-center tw-text-xs tw-text-gray-500">
          {{ emptyText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick } from 'vue';
import { useStore } from 'vuex';

export default {
    name: 'BaseSelect',
    props: {
        modelValue: { default: null },
        options: { type: Array, default: () => [] },
        grouped: { type: Boolean, default: false },
        groupLabel: { type: String, default: 'label' },
        groupOptions: { type: String, default: 'options' },
        optionLabel: { type: String, default: 'label' },
        optionValue: { type: String, default: '' },
        placeholder: { type: String, default: 'Selecione...' },
        searchable: { type: Boolean, default: false },
        searchPlaceholder: { type: String, default: 'Buscar...' },
        emptyText: { type: String, default: 'Nenhum item encontrado.' },
        disabled: { type: Boolean, default: false },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const store = useStore();
        const theme = computed(() => store.state.ui.theme);
        const isDark = computed(() => theme.value === 'dark');

        const open = ref(false);
        const search = ref('');
        const searchInput = ref(null);

        const getLabel = (option) => {
            if (option === null || option === undefined) return '';
            if (typeof option === 'object') return option[props.optionLabel] ?? '';
            return String(option);
        };
        const getValue = (option) => {
            if (props.optionValue && option && typeof option === 'object') return option[props.optionValue];
            return option;
        };
        const isSelected = (option) => getValue(option) === props.modelValue;

        const normalizedGroups = computed(() => {
            if (props.grouped) {
                return (props.options || []).map(g => ({
                    label: g[props.groupLabel],
                    options: g[props.groupOptions] || []
                }));
            }
            return [{ label: null, options: props.options || [] }];
        });

        const filteredGroups = computed(() => {
            const q = search.value.trim().toLowerCase();
            return normalizedGroups.value
                .map(g => ({
                    label: g.label,
                    options: q ? g.options.filter(o => getLabel(o).toLowerCase().includes(q)) : g.options
                }))
                .filter(g => g.options.length > 0);
        });

        const allOptions = computed(() => normalizedGroups.value.flatMap(g => g.options));
        const selectedOption = computed(() => allOptions.value.find(o => isSelected(o)) ?? null);

        const toggle = () => { open.value ? close() : openMenu(); };
        const openMenu = () => {
            if (props.disabled) return;
            open.value = true;
            if (props.searchable) nextTick(() => searchInput.value?.focus());
        };
        const close = () => { open.value = false; search.value = ''; };
        const select = (option) => {
            emit('update:modelValue', getValue(option));
            close();
        };

        return {
            theme, isDark, open, search, searchInput,
            getLabel, getValue, isSelected,
            filteredGroups, selectedOption,
            toggle, close, select
        };
    }
};
</script>

<style scoped>
.base-select-scroll::-webkit-scrollbar { width: 4px; }
.base-select-scroll::-webkit-scrollbar-track { background: transparent; }
.base-select-scroll::-webkit-scrollbar-thumb { background: #33b35a; border-radius: 10px; }
</style>
